// @vero/platform — what the app and the platform server share: plan catalogue shape, licence token format and
// its offline verification (WebCrypto only, so it runs in browsers, the desktop webview and Node), licence
// status rules (trial, active, grace, expired), and the account API types.
//
// Licence token = base64url(JSON payload) "." base64url(ECDSA P-256 / SHA-256 signature).
// The private signing key exists only on the platform server; the app holds the public key. Nothing secret
// ships in client code, and a valid licence keeps working offline until it expires (plus the grace period).

export const PLAN_IDS = ['trial', 'solo', 'professional', 'team', 'enterprise'] as const;
export type PlanId = typeof PLAN_IDS[number] | string;
export const FEATURES = ['sync', 'cloud_backup', 'campaigns', 'tax_center', 'team', 'branding_agency', 'sso', 'audit_export', 'api',
  // 0.6: website integration and subscription matrix (packages/platform/src/pricing.ts)
  'website_inbox', 'client_portal', 'ai_drafts', 'automations', 'analytics', 'group_plans',
  // requirements 01–04 (AVRASTRO Video): an ADD-ON for CRM plans (not in any plan by default) and the standalone video plans
  'video', 'scheduling',
  // requirements 06–11 (Oct 2026): the AI Content Studio — an add-on to the CRM plans, or the only feature of the standalone studio_* plans
  'studio'] as const;
export type Feature = typeof FEATURES[number];
/** features that only an add-on or a standalone plan grants (never part of a CRM plan by default) */
export const ADDON_ONLY: readonly Feature[] = ['video', 'scheduling', 'studio'];

export interface Plan {
  id: PlanId; name: string; interval: 'month' | 'year' | 'trial' | 'custom';
  priceCents: number | null; currency: string; seats: number; devices: number; features: Feature[]; active: boolean;
  /** billing provider price id (Stripe/Paddle) — configured by the owner, never invented */
  externalPriceId?: string | null;
  /** 0.6: provider price ids per billing interval (set by `main.ts stripe-setup` or the owner) */
  prices?: { month?: string | null; year?: string | null };
}
/** Default catalogue (prices are set by the owner in the platform admin; null = not on sale yet). */
export const DEFAULT_PLANS: Plan[] = [
  { id: 'trial', name: 'Trial', interval: 'trial', priceCents: 0, currency: 'EUR', seats: 1, devices: 3, features: ['sync', 'cloud_backup', 'campaigns', 'tax_center'], active: true },
  { id: 'solo', name: 'Solo', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 2, features: ['cloud_backup', 'campaigns', 'tax_center'], active: true },
  { id: 'professional', name: 'Professional', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 5, features: ['sync', 'cloud_backup', 'campaigns', 'tax_center'], active: true },
  { id: 'team', name: 'Team / Agency', interval: 'month', priceCents: null, currency: 'EUR', seats: 5, devices: 5, features: ['sync', 'cloud_backup', 'campaigns', 'tax_center', 'team', 'branding_agency'], active: true },
  { id: 'enterprise', name: 'Enterprise', interval: 'custom', priceCents: null, currency: 'EUR', seats: 25, devices: 10, features: FEATURES.filter((f) => !ADDON_ONLY.includes(f)), active: true },
  // requirement 04 / owner decision D11: the standalone AVRASTRO Video plans (video + scheduling, no CRM modules) are BUILT
  // but not on sale (active: false) until the owner launches them; `video_therapy` (requirement 04, V5) additionally needs a
  // legal review before it may be sold (docs/video/FEASIBILITY.md §8)
  { id: 'video_solo', name: 'AVRASTRO Video Solo', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 3, features: ['video', 'scheduling'], active: false },
  { id: 'video_team', name: 'AVRASTRO Video Team', interval: 'month', priceCents: null, currency: 'EUR', seats: 5, devices: 3, features: ['video', 'scheduling', 'team'], active: false },
  { id: 'video_therapy', name: 'AVRASTRO Video Confidential (requires legal review before sale)', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 3, features: ['video', 'scheduling'], active: false },
  // requirement 10: the standalone AVRASTRO Studio (no CRM features, no sync). Not on sale until the owner sets prices and
  // activates them (`main.ts plan studio_starter --active`); activation codes for a pilot work before that.
  { id: 'studio_starter', name: 'AVRASTRO Studio', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 2, features: ['studio'], active: false },
  { id: 'studio_pro', name: 'AVRASTRO Studio Pro', interval: 'month', priceCents: null, currency: 'EUR', seats: 1, devices: 3, features: ['studio'], active: false },
];
/** plans whose meetings run in the confidential (therapist-grade) mode: E2EE, PIN option, EU-only media, no names kept */
export const CONFIDENTIAL_VIDEO_PLANS: readonly string[] = ['video_therapy'];
/** the standalone video plans (no CRM modules) */
export const STANDALONE_VIDEO_PLANS: readonly string[] = ['video_solo', 'video_team', 'video_therapy'];

export const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'canceled', 'expired'] as const;
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

export interface LicencePayload {
  v: 1; lid: string; ws: string; sub: string; dev: string; plan: PlanId; status: SubscriptionStatus;
  features: Feature[]; seats: number; devices: number;
  /** issued at, paid-until (or trial end), grace end — ISO strings */
  iat: string; exp: string; grace: string;
}
export type LicenceState = 'none' | 'trialing' | 'active' | 'grace' | 'expired' | 'invalid';

const enc = new TextEncoder();
export const b64u = {
  enc(bytes: Uint8Array): string { let s = ''; for (const b of bytes) s += String.fromCharCode(b); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); },
  dec(s: string): Uint8Array { const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)); const out = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i); return out; },
};
const subtle = () => globalThis.crypto.subtle;

/** Parse without verifying (to show details) — never trust the result before `verifyLicence`. */
export function decodeLicence(token: string): LicencePayload | null {
  try { const [p] = token.split('.'); return JSON.parse(new TextDecoder().decode(b64u.dec(p!))) as LicencePayload; } catch { return null; }
}
/** Verify the signature with the platform public key (JWK, ECDSA P-256). */
export async function verifyLicence(token: string, publicJwk: JsonWebKey): Promise<LicencePayload | null> {
  try {
    const [p, sig] = token.split('.');
    if (!p || !sig) return null;
    const key = await subtle().importKey('jwk', publicJwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
    const ok = await subtle().verify({ name: 'ECDSA', hash: 'SHA-256' }, key, b64u.dec(sig) as BufferSource, enc.encode(p) as BufferSource);
    if (!ok) return null;
    const payload = decodeLicence(token);
    return payload && payload.v === 1 ? payload : null;
  } catch { return null; }
}
/** What the app may do under a licence at a given moment. Expired never deletes or hides data: it only stops writes. */
export function licenceState(p: LicencePayload | null, now = new Date()): LicenceState {
  if (!p) return 'none';
  const t = now.toISOString();
  if (p.status === 'canceled' || p.status === 'expired') return t <= p.grace ? 'grace' : 'expired';
  if (t <= p.exp) return p.status === 'trialing' ? 'trialing' : 'active';
  return t <= p.grace ? 'grace' : 'expired';
}
export const daysLeft = (iso: string, now = new Date()) => Math.max(0, Math.ceil((Date.parse(iso) - now.getTime()) / 864e5));

// --- account API types -----------------------------------------------------------------------------
export interface AccountProfile {
  firstName: string; lastName: string; email: string; phone?: string | null; country?: string | null; language?: string | null; currency?: string | null;
  timeZone?: string | null; licenceNumber?: string | null; licenceExpiry?: string | null; licenceAuthority?: string | null; company?: string | null;
  branch?: string | null; address?: string | null; officePhone?: string | null; website?: string | null; emailVerified?: boolean;
}
export interface RegisterInput extends Omit<AccountProfile, 'emailVerified'> {
  password: string; deviceId: string; activationCode?: string | null; workspaceName?: string | null;
  acceptTerms: boolean; acceptPrivacy: boolean; marketingConsent?: boolean;
}
export interface AccountSession {
  token: string; userId: string; role: string; workspaceId: string; workspaceName: string; expiresAt: string; leaseUntil: string;
  licence: string; publicKey: JsonWebKey;
}
export interface PlatformInfo {
  /** 24 Oct 2026: the reviewers' demo account is live (demo-account.ts) — only the last day, nothing else */
  demo?: { until: string } | null;
  name: string; version: string; publicKey: JsonWebKey; plans: Plan[]; billing: string[]; trialDays: number; graceDays: number; registration: 'open' | 'code' | 'closed';
  /** 0.6: the connected website (single sign-on via avrastro.com) and whether AI drafting is configured */
  website?: { url: string; /** 0.7: the website's plugin has called the platform recently (absent on older platforms = assume yes) */ connected?: boolean } | null; ai?: boolean;
  /** Oct 2026: members can connect their Outlook (Microsoft 365) calendar */ outlook?: boolean;
  /** ideas track 43: the owner turned on a text-recognition provider for business-card photos */ ocr?: boolean;
  /** ideas track 37: meeting summary from voice (a transcription provider and the AI provider are configured) */ voice?: boolean;
  /** Oct 2026: sign-in with passkeys (Face ID / Touch ID / fingerprint) */ passkeys?: boolean;
  /** Oct 2026: Web Push — the platform's VAPID public key (null: notifications off) */ push?: string | null;
  /** requirements 01–04: AVRASTRO Video is configured on this platform (a LiveKit server); the right to use it is the add-on */
  video?: boolean;
  /** requirements 06–11: the AI Content Studio has at least one provider configured (each workspace still needs the `studio` right) */ studio?: boolean }
export interface DeviceInfo { deviceId: string; expiresAt: string; revoked: boolean; current: boolean; lastSeen?: string | null; label?: string | null }
export interface TeamMember { userId: string; email: string; name: string; role: string; disabled: boolean; devices: number }
export interface Invitation { id: string; email: string | null; role: string; expiresAt: string; uses: number; maxUses: number }
export interface CloudBackupMeta { id: string; createdAt: string; size: number; label: string | null; userId: string }

/** Minimal password policy shared by client (instant feedback) and server (enforcement). */
const COMMON = ['password', 'password1', '1234567890', 'qwertyuiop', 'iloveyou', 'insurance', 'vero12345', 'welcome123', 'letmein123'];
export function passwordProblem(pw: string, email = ''): 'too_short' | 'too_common' | 'contains_email' | null {
  if (pw.length < 10) return 'too_short';
  const low = pw.toLowerCase();
  if (COMMON.some((c) => low === c || low.startsWith(c))) return 'too_common';
  const local = email.split('@')[0]?.toLowerCase();
  if (local && local.length >= 4 && low.includes(local)) return 'contains_email';
  return null;
}

export * from './client.ts';
// 17 Oct 2026: the assistants' normaliser (pure; the CRM's help assistant ranks its sections with it too)
export * from './text-normalise.ts';
// requirement 20: the one entitlement map and the package-specific release notes; requirement 21: maintenance notices
export * from './entitlements.ts';
export * from './release-notes.ts';
export * from './notices-shared.ts';
